import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { Activity, Loader2, MonitorSmartphone, ShieldOff } from 'lucide-react';
import { Drawer } from '@components/Drawer';
import { Button } from '@components/Button';
import { Alert, ConfirmDialog } from '@components/Dialog';
import { ProviderIcon } from '@components/ProviderIcon';
import profileService from '@services/profileService';
import {
  USAGE_MODULES,
  USAGE_MODULE_LABELS,
  type AppUser,
  type UsageReport,
  type UserSession,
} from '@/types/planning';
import { formatDateBR, formatDateTimeBR, localTimestamp, plural } from '@/utils/formatters';
import { summarizeUserAgent } from '@/utils/userAgent';

interface Props {
  user: AppUser;
  onClose: () => void;
  onError: (err: unknown) => void;
}

const DAY_OPTIONS = [7, 30, 90] as const;
type Days = (typeof DAY_OPTIONS)[number];

type SessionState = 'current' | 'active' | 'revoked' | 'expired';

/** Situação da sessão: atual (a do admin), revogada, expirada (`expires_at` no passado) ou ativa. */
const sessionState = (s: UserSession, now: string): SessionState => {
  if (s.revoked_at) return 'revoked';
  if (s.expires_at && s.expires_at < now) return 'expired';
  if (s.is_current) return 'current';
  return 'active';
};

const SESSION_BADGE: Record<SessionState, { cls: string; label: string }> = {
  current: { cls: 'badge-success', label: 'Atual' },
  active: { cls: 'badge-info', label: 'Ativa' },
  revoked: { cls: 'badge-neutral', label: 'Revogada' },
  expired: { cls: 'badge-warning', label: 'Expirada' },
};

const moduleLabel = (m: string): string => USAGE_MODULE_LABELS[m as keyof typeof USAGE_MODULE_LABELS] ?? m;

const fmtInt = (n: number) => n.toLocaleString('pt-BR');

/**
 * "Atividade" de um usuário (Administração → Usuários): sessões (GET /admin/users/:id/sessions) com
 * "Encerrar todas as sessões" (POST …/sessions/revoke) e uso por módulo (GET …/usage?days=N) —
 * totais em barras e a tabela dia × módulo. Só cadastro, sessões e contadores: nenhum dado pessoal.
 */
export const UserActivityDrawer: React.FC<Props> = ({ user, onClose, onError }) => {
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [usage, setUsage] = useState<UsageReport | null>(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [days, setDays] = useState<Days>(30);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [revokedInfo, setRevokedInfo] = useState<number | null>(null);

  const now = localTimestamp(Date.now());

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      setSessions(await profileService.listUserSessions(user.user_id));
    } catch (err) {
      onError(err);
    } finally {
      setSessionsLoading(false);
    }
  }, [user.user_id, onError]);

  const loadUsage = useCallback(async () => {
    setUsageLoading(true);
    try {
      setUsage(await profileService.getUserUsage(user.user_id, days));
    } catch (err) {
      onError(err);
    } finally {
      setUsageLoading(false);
    }
  }, [user.user_id, days, onError]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  const activeCount = sessions.filter((s) => {
    const st = sessionState(s, now);
    return st === 'active' || st === 'current';
  }).length;

  const revoke = async () => {
    setRevoking(true);
    try {
      const { revoked } = await profileService.revokeUserSessions(user.user_id);
      setConfirmRevoke(false);
      setRevokedInfo(revoked);
      await loadSessions();
    } catch (err) {
      setConfirmRevoke(false);
      onError(err);
    } finally {
      setRevoking(false);
    }
  };

  // Totais por módulo (ordem fixa dos módulos; os sem uso ficam com zero) e tabela dia × módulo.
  const totals = useMemo(() => {
    const t = usage?.totals ?? {};
    const known = USAGE_MODULES.map((m) => ({ module: m, label: USAGE_MODULE_LABELS[m], requests: t[m] ?? 0 }));
    const extra = Object.keys(t)
      .filter((k) => !USAGE_MODULES.includes(k as (typeof USAGE_MODULES)[number]))
      .map((k) => ({ module: k, label: moduleLabel(k), requests: t[k] ?? 0 }));
    return [...known, ...extra];
  }, [usage]);
  const maxTotal = Math.max(1, ...totals.map((t) => t.requests));

  const usedModules = useMemo(() => totals.filter((t) => t.requests > 0).map((t) => t.module), [totals]);

  const dayRows = useMemo(() => {
    const byDate = new Map<string, Record<string, number>>();
    for (const r of usage?.rows ?? []) {
      const row = byDate.get(r.date) ?? {};
      row[r.module] = (row[r.module] ?? 0) + r.requests;
      byDate.set(r.date, row);
    }
    return [...byDate.entries()]
      .sort((a, b) => (a[0] < b[0] ? 1 : a[0] > b[0] ? -1 : 0))
      .map(([date, row]) => ({ date, row, total: Object.values(row).reduce((a, b) => a + b, 0) }));
  }, [usage]);

  return (
    <Drawer
      isOpen
      onClose={onClose}
      size="xl"
      closeOnEsc={!confirmRevoke}
      title={
        <span className="inline-flex items-center gap-2 min-w-0">
          <Activity size={18} className="text-slate-500 flex-shrink-0" />
          <span className="truncate">Atividade de {user.name}</span>
        </span>
      }
    >
      <div className="space-y-6">
        <p className="text-small [overflow-wrap:anywhere]">
          {user.email} · <ProviderIcon provider={user.provider} size={12} withLabel />. Aqui aparecem só sessões e contadores de uso —
          nenhum dado da agenda, dos documentos ou das finanças.
        </p>

        {/* ───────── Sessões ───────── */}
        <section className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-h4 flex items-center gap-2">
              <MonitorSmartphone size={16} className="text-slate-500" /> Sessões
              {!sessionsLoading && <span className="badge-neutral">{plural(activeCount, 'ativa', 'ativas')}</span>}
            </h3>
            <Button
              variant="outline"
              size="sm"
              icon={<ShieldOff />}
              onClick={() => setConfirmRevoke(true)}
              disabled={sessionsLoading || activeCount === 0}
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              Encerrar todas as sessões
            </Button>
          </div>

          {revokedInfo !== null && (
            <Alert variant="success" onClose={() => setRevokedInfo(null)}>
              {revokedInfo === 0 ? 'Nenhuma sessão ativa para encerrar.' : `${plural(revokedInfo, 'sessão encerrada', 'sessões encerradas')}. O usuário precisará entrar de novo.`}
            </Alert>
          )}

          {sessionsLoading && sessions.length === 0 && (
            <p className="text-center text-slate-400 py-4">
              <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
            </p>
          )}
          {!sessionsLoading && sessions.length === 0 && <p className="text-small text-slate-500">Nenhuma sessão registrada.</p>}

          {/* Celular: cartões */}
          <div className="md:hidden space-y-2">
            {sessions.map((s) => {
              const st = sessionState(s, now);
              return (
                <div key={s.session_id} className={clsx('card p-3 space-y-1 text-sm', (st === 'revoked' || st === 'expired') && 'opacity-70')}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 min-w-0">
                      <ProviderIcon provider={s.provider} size={12} />
                      <span className="truncate">{summarizeUserAgent(s.user_agent)}</span>
                    </span>
                    <span className={SESSION_BADGE[st].cls}>{SESSION_BADGE[st].label}</span>
                  </div>
                  <p className="text-small">Entrada {formatDateTimeBR(s.created_at)} · último acesso {formatDateTimeBR(s.last_seen_at) || '—'}</p>
                  <p className="text-small [overflow-wrap:anywhere]">
                    IP {s.ip_address || '—'} · {plural(s.request_count ?? 0, 'requisição', 'requisições')}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Tabela */}
          {sessions.length > 0 && (
            <div className="table-container bg-white hidden md:block">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="table-header">
                    <tr>
                      <th>Entrada</th>
                      <th>Último acesso</th>
                      <th>Provedor</th>
                      <th>IP</th>
                      <th>Navegador</th>
                      <th className="text-right">Requisições</th>
                      <th>Situação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((s) => {
                      const st = sessionState(s, now);
                      return (
                        <tr key={s.session_id} className={clsx('table-row', (st === 'revoked' || st === 'expired') && 'opacity-70')}>
                          <td className="table-cell whitespace-nowrap">{formatDateTimeBR(s.created_at)}</td>
                          <td className="table-cell whitespace-nowrap">{formatDateTimeBR(s.last_seen_at) || '—'}</td>
                          <td className="table-cell">
                            <ProviderIcon provider={s.provider} size={12} withLabel />
                          </td>
                          <td className="table-cell whitespace-nowrap">{s.ip_address || '—'}</td>
                          <td className="table-cell" title={s.user_agent ?? undefined}>
                            {summarizeUserAgent(s.user_agent)}
                          </td>
                          <td className="table-cell text-right tabular-nums">{fmtInt(s.request_count ?? 0)}</td>
                          <td className="table-cell">
                            <span className={SESSION_BADGE[st].cls} title={s.revoked_at ? `Revogada em ${formatDateTimeBR(s.revoked_at)}${s.revoked_by ? ` por ${s.revoked_by}` : ''}` : `Expira em ${formatDateTimeBR(s.expires_at)}`}>
                              {SESSION_BADGE[st].label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        {/* ───────── Uso ───────── */}
        <section className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-h4 flex items-center gap-2">
              <Activity size={16} className="text-slate-500" /> Uso
              {usage && !usageLoading && <span className="badge-neutral">{plural(usage.total, 'requisição', 'requisições')}</span>}
            </h3>
            <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" role="radiogroup" aria-label="Período">
              {DAY_OPTIONS.map((d) => (
                <button
                  key={d}
                  type="button"
                  role="radio"
                  aria-checked={days === d}
                  onClick={() => setDays(d)}
                  className={clsx('px-3 py-1 text-xs font-medium transition-colors', days === d ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50')}
                >
                  {d} dias
                </button>
              ))}
            </div>
          </div>
          {usage && (usage.from || usage.to) && (
            <p className="text-small">
              De {formatDateBR(usage.from)} a {formatDateBR(usage.to)}. Contado por módulo a cada requisição, gravado a cada 60 s.
            </p>
          )}

          {usageLoading && !usage && (
            <p className="text-center text-slate-400 py-4">
              <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
            </p>
          )}

          {usage && (
            <>
              {/* Totais por módulo: barras horizontais simples */}
              <div className="space-y-1.5">
                {totals.map((t) => (
                  <div key={t.module} className="grid grid-cols-[7rem_1fr_3.5rem] sm:grid-cols-[9rem_1fr_4rem] items-center gap-2 text-sm">
                    <span className="truncate text-slate-700">{t.label}</span>
                    <div className="h-3 rounded bg-slate-100 overflow-hidden">
                      <div
                        className={clsx('h-full rounded', t.requests > 0 ? 'bg-emerald-500' : 'bg-transparent')}
                        style={{ width: `${Math.max(t.requests > 0 ? 2 : 0, Math.round((t.requests / maxTotal) * 100))}%` }}
                      />
                    </div>
                    <span className="text-right tabular-nums text-slate-600">{fmtInt(t.requests)}</span>
                  </div>
                ))}
              </div>

              {/* Dia × módulo (últimos dias primeiro) */}
              {dayRows.length === 0 ? (
                <p className="text-small text-slate-500">Sem uso no período.</p>
              ) : (
                <div className="table-container bg-white">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="table-header">
                        <tr>
                          <th className="sticky left-0 bg-slate-50 z-10">Dia</th>
                          {usedModules.map((m) => (
                            <th key={m} className="text-right">
                              {moduleLabel(m)}
                            </th>
                          ))}
                          <th className="text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dayRows.map((r) => (
                          <tr key={r.date} className="table-row">
                            <td className="table-cell whitespace-nowrap sticky left-0 bg-white z-10">{formatDateBR(r.date)}</td>
                            {usedModules.map((m) => (
                              <td key={m} className="table-cell text-right tabular-nums">
                                {r.row[m] ? fmtInt(r.row[m]) : <span className="text-slate-300">—</span>}
                              </td>
                            ))}
                            <td className="table-cell text-right tabular-nums font-medium">{fmtInt(r.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      <ConfirmDialog
        isOpen={confirmRevoke}
        onClose={() => setConfirmRevoke(false)}
        onConfirm={revoke}
        loading={revoking}
        severity="danger"
        title="Encerrar todas as sessões?"
        message={`Todas as sessões ativas de ${user.name} serão revogadas e a pessoa precisará entrar de novo em cada aparelho. Os dados não são afetados.`}
        confirmText="Encerrar sessões"
      />
    </Drawer>
  );
};

export default UserActivityDrawer;
